import { Divider, FlexLayout, H2, H4 } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord, mapRecordToArray } from '@deity-io/di-data-basics';
import { Icon } from '@deity-io/portal-ui';
import { PlayArrow } from '@mui/icons-material';
import type { FC } from 'react';
import type { FlowEntityType } from 'services/graphql/generated/schemas';
import type { IClusterStatePackage, IExternalInstance, IProducerInstance, IRuntimeState } from '../IRuntimeState';
import { isIProducerInstance } from '../IRuntimeState';
import { PackagePopoutExternal } from './PackagePopoutExternal';
import { PackagePopoutProducer } from './PackagePopoutProducer';

type PackagePopoutFlowProps = {
  pipeState: IClusterStatePackage['versions'][0]['pipelines'][0];
  flowData: FlowEntityType;
  accountUuid: string;
  packageVersionUuid: string;
  services: IRuntimeState['services'];
};

export function isIButtonProducer(
  value: IProducerInstance['producers'][0]
): value is IProducerInstance['producers'][0] {
  return value.type === 'button';
}

export function isIExternalInstance(value: any): value is IExternalInstance {
  return 'components' in value;
}

export const PackagePopoutFlow: FC<PackagePopoutFlowProps> = ({
  pipeState: pipeline,
  flowData,
  accountUuid,
  packageVersionUuid,
  services
}) => {
  const groupedProducers: Record<
    string,
    {
      type: string;
      since: number;
      count: number;
      instanceCount: number;
      initialized: number;
      instances: Record<string, IPropertyBag>;
    }
  > = {};

  const groupedExternals: Record<
    string,
    {
      method: string;
      path: string;
      since: number;
      last: number;
      count: number;
      instanceCount: number;
      initialized: number;
      instances: Record<string, IPropertyBag>;
    }
  > = {};

  const states = {};

  mapRecord(pipeline.instances, (instance, instanceKey) => {
    if (instance.data.state === undefined) {
      // eslint-disable-next-line no-console
      console.warn('Undefined state in PackagePopoutFlow');
    }
    if (!(instance.data.state in states)) {
      states[instance.data.state] = 0;
    }
    states[instance.data.state] += 1;
    if (isIProducerInstance(instance.data)) {
      mapRecord(instance.data.producers, producer => {
        if (!(producer.key in groupedProducers)) {
          groupedProducers[producer.key] = {
            type: producer.type,
            since: producer.since,
            count: 0,
            instanceCount: 0,
            initialized: 0,
            instances: {}
          };
        }
        const groupedProducer = groupedProducers[producer.key];
        groupedProducer.instances[instanceKey] = producer;
        groupedProducer.count += producer.count;
        groupedProducer.instanceCount += 1;
        if (groupedProducer.since > producer.since) {
          groupedProducer.since = producer.since;
        }
        if (producer.initialized) {
          groupedProducer.initialized += 1;
        }
      });
    } else if (isIExternalInstance(instance.data)) {
      mapRecord(instance.data.components, producer => {
        if (!(producer.component in groupedExternals)) {
          groupedExternals[producer.component] = {
            method: producer.method,
            path: producer.path,
            count: 0,
            instanceCount: 0,
            initialized: 0,
            instances: {},
            since: Date.now(),
            last: 0
          };
        }
        const groupedExternal = groupedExternals[producer.component];
        groupedExternal.instances[instanceKey] = producer;

        groupedExternal.count += producer.count || 0;
        if (groupedExternal.since > producer.since) {
          groupedExternal.since = producer.since;
        }
        if (producer.last && groupedExternal.last < producer.last) {
          groupedExternal.last = producer.last;
        }

        groupedExternal.instanceCount += 1;

        // if (producer.initialized) {
        //   groupedProducer.initialized++;
        // }
      });
    } else {
      // eslint-disable-next-line no-console
      console.log('instance.data is not IProducerInstance', instance.data);
    }
  });

  let itemCount = 0;

  return (
    <FlexLayout flexDirection="column" width="full">
      <FlexLayout key="flexHeader" flexDirection="row" width="full">
        <PlayArrow fontSize="large" />
        <H2>{flowData?.name || pipeline.uuid}</H2>
        {mapRecordToArray(states, (count, stateName) => (
          <FlexLayout ml="lg">
            <Icon icon="cloud" />
            <H4 fontSize="lg" color="primaryText" m="xs">
              {count} x {stateName}
            </H4>
          </FlexLayout>
        ))}
      </FlexLayout>
      <Divider key="flexDivider" css={{ borderColor: '#DADFE4' }} />
      <FlexLayout key="flexPopout" flexDirection="row">
        {mapRecordToArray(groupedProducers, (producer, producerKey) => {
          itemCount += 1;

          return (
            <PackagePopoutProducer
              key={producerKey}
              producer={producer}
              producerKey={producerKey}
              flowKey={flowData?.uuid}
              pipelineKey={pipeline.uuid}
              accountUuid={accountUuid}
              packageVersionUuid={packageVersionUuid}
              services={services}
            />
          );
        })}
        {mapRecordToArray(groupedExternals, (external, externalKey) => {
          itemCount += 1;

          return (
            <PackagePopoutExternal
              key={externalKey}
              component={external}
              componentKey={externalKey}
              flowKey={flowData?.uuid}
              pipelineKey={pipeline.uuid}
              accountUuid={accountUuid}
              packageVersionUuid={packageVersionUuid}
              services={services}
            />
          );
        })}
        {itemCount === 0 ? (
          <FlexLayout key="flexDisposed" flexDirection="row" width="full" justifyContent="center">
            <H2>Disposed</H2>
          </FlexLayout>
        ) : null}
      </FlexLayout>
    </FlexLayout>
  );
};
